import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import InteractionShield from './InteractionShield';
import MustWatchVideoModal from './MustWatchVideoModal';
import {
  MustWatchVideoNotification,
  MustWatchVideoTranslations,
  VideoNotificationDto,
  VIDEO_NOTIFICATION_BELL_CLASS,
  VIDEO_NOTIFICATION_PANEL_CLASS,
} from './constants';
import {
  getUnseenMustWatch,
  getUnseenNotifications,
  getUnseenOptional,
  normalizeVideoNotifications,
} from './helpers';
import { fetchVideoNotificationHistory, VideoNotificationHistoryPage } from './videoNotificationApi';

interface VideoNotificationsContextValue {
  /** every active notification, panel sort order applied */
  notifications: MustWatchVideoNotification[];
  unseenCount: number;
  /** unseen must-watch queue — non-empty means the UI is locked */
  unseenMustWatch: MustWatchVideoNotification[];
  isLocked: boolean;
  isPanelOpen: boolean;
  setPanelOpen: (open: boolean) => void;
  activeNotification: MustWatchVideoNotification | null;
  openNotification: (id: number) => void;
  t: MustWatchVideoTranslations | null;
  /** load updates older than the 30-day feed (appended to `notifications`) */
  loadOlder: () => void;
  hasMoreHistory: boolean;
  isLoadingHistory: boolean;
}

const noop = () => undefined;

const VideoNotificationsContext =
  createContext<VideoNotificationsContextValue>({
    notifications: [],
    unseenCount: 0,
    unseenMustWatch: [],
    isLocked: false,
    isPanelOpen: false,
    setPanelOpen: noop,
    activeNotification: null,
    openNotification: noop,
    t: null,
    loadOlder: noop,
    hasMoreHistory: false,
    isLoadingHistory: false,
  });

export const useVideoNotifications = () =>
  useContext(VideoNotificationsContext);

interface Props {
  /** pending queue from GET /ms/video-notifications/active */
  notifications?: VideoNotificationDto[] | null;
  /**
   * Host gate for the whole feature — when false there are no notifications,
   * so the bell is not rendered and nothing can lock the UI.
   * B2C passes "agent, in agent mode"; Backoffice passes "logged-in agency
   * agent".
   */
  enabled?: boolean;
  t: MustWatchVideoTranslations;
  /**
   * Fired when the user confirms they watched a video. Hosts post
   * /ms/video-notifications/:id/watched here; the lock is released locally as
   * soon as it resolves, without waiting for a refetch.
   */
  onDone: (notification: MustWatchVideoNotification) => void | Promise<void>;
  children: React.ReactNode;
}

/**
 * Owns the video-notification state and renders the interaction shield (while
 * an unseen must-watch video exists) plus the video modal.
 *
 * The bell and its panel are rendered by the host app through
 * `<VideoNotificationBell />`, so they can sit exactly where they belong
 * (agent banner in B2C, header actions in Backoffice).
 */
export const VideoNotificationsProvider: React.FC<Props> = ({
  notifications: rawNotifications,
  enabled = true,
  t,
  onDone,
  children,
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [isDoneLoading, setIsDoneLoading] = useState(false);
  /** confirmed this session — the next refetch returns them with isWatched */
  const [watchedIds, setWatchedIds] = useState<number[]>([]);
  /** older updates loaded with "load older" - kept until the provider unmounts */
  const [history, setHistory] = useState<VideoNotificationDto[]>([]);
  const [historyCursor, setHistoryCursor] = useState<VideoNotificationHistoryPage['next_cursor']>(null);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const loadOlder = useCallback(async () => {
    if (isLoadingHistory || !hasMoreHistory) {
      return;
    }
    setIsLoadingHistory(true);
    try {
      const page = await fetchVideoNotificationHistory(historyCursor);
      setHistory((prev) => [...prev, ...page.videos.filter((v) => !prev.some((p) => p.id === v.id))]);
      setHistoryCursor(page.next_cursor);
      setHasMoreHistory(page.has_more);
    } catch (e) {
      console.warn('video notifications history', e);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [isLoadingHistory, hasMoreHistory, historyCursor]);

  const notifications = useMemo(() => {
    // `enabled` switches the whole feature off, not just the lock — with no
    // notifications the bell renders nothing, so users who fail the host's
    // permission check never see it in their header.
    if (!enabled) {
      return [];
    }
    // the feed wins over history for an id that is in both
    const feed = Array.isArray(rawNotifications) ? rawNotifications : [];
    const older = history.filter((h) => !feed.some((n) => n.id === h.id));
    const normalized = normalizeVideoNotifications([...feed, ...older]);
    if (!watchedIds.length) {
      return normalized;
    }
    // Re-sorting is intentional: a video that was just watched drops out of the
    // unseen group, which is what the panel order is based on.
    return normalizeVideoNotifications(
      normalized.map((n) =>
        watchedIds.includes(n.id) ? { ...n.raw, isWatched: true } : n.raw
      )
    );
  }, [enabled, rawNotifications, history, watchedIds]);

  const unseen = useMemo(
    () => getUnseenNotifications(notifications),
    [notifications]
  );
  const unseenMustWatch = useMemo(
    () => getUnseenMustWatch(notifications),
    [notifications]
  );
  const unseenOptional = useMemo(
    () => getUnseenOptional(notifications),
    [notifications]
  );

  // `enabled` is already folded into `notifications` above.
  const isLocked = unseenMustWatch.length > 0;

  const activeNotification = useMemo(
    () => notifications.find((n) => n.id === activeId) ?? null,
    [notifications, activeId]
  );

  // A notification that disappears from the list (queue refetch) must not
  // leave a dangling modal behind.
  useEffect(() => {
    if (activeId !== null && !activeNotification) {
      setActiveId(null);
    }
  }, [activeId, activeNotification]);

  const openNotification = useCallback((id: number) => {
    setActiveId(id);
    setIsPanelOpen(false);
  }, []);

  const closeModal = useCallback(() => setActiveId(null), []);

  /**
   * Arrows walk the unseen queue the open video belongs to — required videos
   * navigate other required ones, optional videos navigate other optional
   * ones. The two groups never mix, so the arrows never pull a user out of a
   * required run into an optional one. Already-watched videos open on their
   * own, with no arrows.
   */
  const queue = useMemo(() => {
    if (!activeNotification || activeNotification.isWatched) {
      return [];
    }
    return activeNotification.isMustWatch ? unseenMustWatch : unseenOptional;
  }, [activeNotification, unseenMustWatch, unseenOptional]);

  const queueIndex = useMemo(
    () => queue.findIndex((n) => n.id === activeId),
    [queue, activeId]
  );

  const goToQueueIndex = useCallback(
    (index: number) => {
      const next = queue[index];
      if (next) {
        setActiveId(next.id);
      }
    },
    [queue]
  );

  const handleDone = useCallback(async () => {
    if (!activeNotification) {
      return;
    }
    setIsDoneLoading(true);
    try {
      await onDone(activeNotification);
      setWatchedIds((prev) =>
        prev.includes(activeNotification.id)
          ? prev
          : [...prev, activeNotification.id]
      );
      // Advance within the same queue so the user is not bounced back to the
      // panel between clips. Dropping the current item shifts its successor
      // into its index; if it was the last one, wrap to the front.
      const remaining = queue.filter((n) => n.id !== activeNotification.id);
      const next = remaining[queueIndex] ?? remaining[0];
      setActiveId(next ? next.id : null);
    } finally {
      setIsDoneLoading(false);
    }
  }, [activeNotification, onDone, queue, queueIndex]);

  const contextValue = useMemo<VideoNotificationsContextValue>(
    () => ({
      notifications,
      unseenCount: unseen.length,
      unseenMustWatch,
      isLocked,
      isPanelOpen,
      setPanelOpen: setIsPanelOpen,
      activeNotification,
      openNotification,
      t,
      loadOlder,
      hasMoreHistory,
      isLoadingHistory,
    }),
    [
      notifications,
      unseen.length,
      unseenMustWatch,
      isLocked,
      isPanelOpen,
      activeNotification,
      openNotification,
      t,
      loadOlder,
      hasMoreHistory,
      isLoadingHistory,
    ]
  );

  /** the modal cannot be dismissed while it shows a required, unseen video */
  const isBlockingActive =
    isLocked &&
    !!activeNotification &&
    activeNotification.isMustWatch &&
    !activeNotification.isWatched;

  return (
    <VideoNotificationsContext.Provider value={contextValue}>
      {children}

      {/* The shield is only needed while the modal is closed — the modal's own
          mask blocks the page once it is open. The panel is portalled above the
          shield, so it stays interactive. */}
      {isLocked && !activeNotification && (
        <InteractionShield
          targetSelector={`.${VIDEO_NOTIFICATION_BELL_CLASS}`}
          allowedSelectors={[
            `.${VIDEO_NOTIFICATION_BELL_CLASS}`,
            `.${VIDEO_NOTIFICATION_PANEL_CLASS}`,
          ]}
          hint={t.blockedHint}
          // Fail open: if the host never rendered a bell (hidden header,
          // unsupported viewport) show the first required video directly
          // instead of trapping the user.
          onTargetMissing={() => setActiveId(unseenMustWatch[0]?.id ?? null)}
        />
      )}

      {!!activeNotification && (
        <MustWatchVideoModal
          open={!!activeNotification}
          notification={activeNotification}
          t={t}
          isDoneLoading={isDoneLoading}
          isBlocking={isBlockingActive}
          queueLength={queue.length}
          queueIndex={queueIndex}
          onPrev={() => goToQueueIndex(queueIndex - 1)}
          onNext={() => goToQueueIndex(queueIndex + 1)}
          onClose={closeModal}
          onDone={handleDone}
        />
      )}
    </VideoNotificationsContext.Provider>
  );
};

export default VideoNotificationsProvider;
